import type {ProductDeals} from '../models/ProductDeals';
import {useUserSelection} from './useUserSelection';
import {useEffect, useMemo, useState} from 'react';
import type {DealSelection} from '../models/DealSelection';
import {DealType} from '../models/DealSelection';

const bulkDealSelection = (productDeals, selectedDeal, quantity) => {
  const price = {
    basePrice: selectedDeal.mejorpreciotachado,
    sellingPrice: selectedDeal.mejorprecio
  };
  const discount = {
    value: (selectedDeal.mejorpreciotachado - selectedDeal.mejorprecio) * quantity,
    percentage: selectedDeal.porcentajeAhorro,
    text: selectedDeal.Descuentos1
  };
  let bcDiscounts = {
    descBc1: 0,
    descBc2: 0,
    descBc3: 0,
    descBc4: 0,
    descBc5: 0,
    descBc6: 0,
    descBc7: 0,
    descBc8: 0,
    descBc9: selectedDeal.descBc9,
    descBc10: 0
  };
  if (selectedDeal.THR_Sales_Price === 0) {
    bcDiscounts = {
      descBc1: selectedDeal.descBc1,
      descBc2: selectedDeal.descBc2,
      descBc3: selectedDeal.descBc3,
      descBc4: selectedDeal.descBc4,
      descBc5: 0,
      descBc6: 0,
      descBc7: 0,
      descBc8: 0,
      descBc9: selectedDeal.descBc9,
      descBc10: 0
    };
  }
  return {
    price,
    discount,
    bcDiscounts
  };
};

const exclusiveDealSelection = (productDeals, selectedDeal, quantity) => {
  const price = {
    basePrice: productDeals.mejorpreciotachado,
    sellingPrice: selectedDeal.price
  };
  const discount = {
    value: (productDeals.mejorpreciotachado - selectedDeal.price) * quantity,
    percentage: selectedDeal.descuento,
    text: selectedDeal.Descuentos1
  };
  const bcDiscounts = {
    descBc1: productDeals.descBc1,
    descBc2: productDeals.descBc2,
    descBc3: productDeals.descBc3,
    descBc4: productDeals.descBc4,
    descBc5: selectedDeal.descBc5,
    descBc6: selectedDeal.descBc6,
    descBc7: selectedDeal.descBc7,
    descBc8: selectedDeal.descBc8,
    descBc9: 0,
    descBc10: 0
  };
  return {
    price,
    discount,
    bcDiscounts
  };
};


const giftDealSelection = (productDeals, deal, quantity) => {
  let price = {
    sellingPrice: deal.RLD_Precio_Producto,
    basePrice: deal.RLD_Precio_Producto
  };
  const discount = {
    percentage: deal.descuento,
    text: deal.Descuentos1
  };
  let bcDiscounts = {
    descBc1: 0,
    descBc2: 0,
    descBc3: 0,
    descBc4: 0,
    descBc5: 0,
    descBc6: 0,
    descBc7: 0,
    descBc8: 0,
    descBc9: 0,
    descBc10: 0
  };
  if (deal.RLD_Precio_Producto === 0) {
    price = {
      sellingPrice: deal.mejorpreciotachado,
      basePrice: deal.mejorprecio
    };
    bcDiscounts = {
      descBc1: deal.descBc1,
      descBc2: deal.descBc2,
      descBc3: deal.descBc3,
      descBc4: deal.descBc4,
      descBc5: productDeals.descBc5,
      descBc6: productDeals.descBc6,
      descBc7: productDeals.descBc7,
      descBc8: productDeals.descBc8,
      descBc9: 0,
      descBc10: deal.descBc10
    };
  }
  return {
    price,
    discount,
    bcDiscounts
  };
};

const noDealSelection = (productDeals, quantity) => {
  const price = {
    basePrice: productDeals.mejorpreciotachado,
    sellingPrice: productDeals.mejorprecio
  };
  const discount = {
    value: (productDeals.mejorpreciotachado - productDeals.mejorprecio) * quantity,
    percentage: null,
    text: productDeals.Descuentos1
  };
  const bcDiscounts = {
    descBc1: productDeals.descBc1 ?? 0,
    descBc2: productDeals.descBc2 ?? 0,
    descBc3: productDeals.descBc3 ?? 0,
    descBc4: productDeals.descBc4 ?? 0,
    descBc5: productDeals.descBc5 ?? 0,
    descBc6: productDeals.descBc6 ?? 0,
    descBc7: productDeals.descBc7 ?? 0,
    descBc8: productDeals.descBc8 ?? 0,
    descBc9: productDeals.descBc9 ?? 0,
    descBc10: productDeals.descBc10 ?? 0
  };
  return {
    price,
    discount,
    bcDiscounts
  };
};

interface Props {
  productDeals: ProductDeals,
}

export const usePurchaseLogic = ({ productDeals }: Props) => {
  const { quantity, dealSelection, onQuantityChange, setDealSelection } = useUserSelection();
  const [price, setPrice] = useState();
  const [discount, setDiscount] = useState();
  const [bcDiscounts, setBcDiscounts] = useState();

  useEffect(() => {
    const setup = (productDeals: ProductDeals) => {
      onQuantityChange(1);
      setDealSelection(null);

      const exclusiveDeal = productDeals.OFERTASEXCLUSIVAS.find(it => it.cantidad === 1);
      let result;
      if (!!exclusiveDeal) {
        result = exclusiveDealSelection(productDeals, exclusiveDeal, quantity);
        setDealSelection({
          type: DealType.EXCLUSIVE_DEAL,
          value: exclusiveDeal
        });
      } else {
        result = noDealSelection(productDeals, quantity);
      }
      setPrice(result.price);
      setDiscount(result.discount);
      setBcDiscounts(result.bcDiscounts);
    };

    if (productDeals) {
      setup(productDeals);
    }
  }, [productDeals]);

  useEffect(() => {
    const selectDeal = (productDeals: ProductDeals, dealSelection: DealSelection, quantity: number) => {
      let result;
      switch (dealSelection?.type) {
        case DealType.BULK_DEAL:
          result = bulkDealSelection(productDeals, dealSelection.value, quantity);
          break;
        case DealType.GIFT_DEAL:
          result = giftDealSelection(productDeals, dealSelection.value, quantity);
          break;
        case DealType.EXCLUSIVE_DEAL:
          result = exclusiveDealSelection(productDeals, dealSelection.value, quantity);
          break;
        default:
          result = noDealSelection(productDeals, quantity);
      }
      setPrice(result.price);
      setDiscount(result.discount);
      setBcDiscounts(result.bcDiscounts);
    };

    if (productDeals) {
      selectDeal(productDeals, dealSelection, quantity);
    }
  }, [dealSelection, productDeals, quantity]);

  return useMemo(() => (
    {
      dealSelection,
      quantity,
      price,
      discount,
      bcDiscounts,
      setDealSelection,
      setQuantity: onQuantityChange
    }
  ), [bcDiscounts, dealSelection, discount, price, quantity]);
};